import type { InventoryVehicle } from '@carvana/merch-contracts';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import isEqual from 'lodash.isequal';
import { useContext, useEffect, useRef } from 'react';
import useGetClientId from '~/constants/srp/getClientId';
import { useUserId, useUserLocation } from '~/context/common/UserContext';
import { useIsDealershipPage } from '~/context/dealership/StaticContext';
import ErrorContext from '~/context/srp/ErrorContext';
import InventoryContext from '~/context/srp/InventoryContext';
import useMemoCompare from '~/hooks/srp/useMemoCompare';
import fetchVehiclesDeliveryPickup, {
  formatVehicleForRequest
} from '~/services/common/fetchVehiclesDeliveryPickup';
import { generateErrorLogger, logError } from '~/utilities/loggers';
import { convertAvailabilityVehiclesToMap } from './helpers';

const useGetAvailability = () => {
  const {
    inventory: {
      data: {
        searchRequestId,
        inventory: { vehicles }
      }
    },
    inventoryBulkData: { setData: setBulkData }
  } = useContext(InventoryContext);

  const { updateResponseErrors } = useContext(ErrorContext);
  const isDealership = useIsDealershipPage();
  const userLocation = useUserLocation();
  const clientId = useGetClientId();
  const userId = useUserId();

  // We use this to ensure we're only re-fetching if the vehicleIds or downPaymentDetails have changed on the vehicles object.
  const stableVehicles = useMemoCompare<Array<InventoryVehicle>>(
    vehicles,
    (prev, next) =>
      isEqual(
        prev.map(vehicle => vehicle.vehicleId),
        next.map(vehicle => vehicle.vehicleId)
      )
  );

  const zipRef = useRef(userLocation.zip);
  useEffect(() => {
    zipRef.current = userLocation.zip;
  }, [userLocation.zip]);

  const shouldCallAvailability = !isDealership && stableVehicles.length;

  useEffectWithController(
    controller => {
      if (!shouldCallAvailability) return;

      fetchVehiclesDeliveryPickup(
        {
          clientId,
          correlationId: searchRequestId,
          userId,
          vehicles: stableVehicles.map(formatVehicleForRequest),
          zip: zipRef.current
        },
        controller
      )
        .then(res => {
          if (res.data.status === 'Succeeded') {
            const vehicleAvailabilities = convertAvailabilityVehiclesToMap(
              res.data.content ?? []
            );

            const didAvailabilitySucceed = Object.values(
              vehicleAvailabilities
            ).some(d => d.soonestDelivery || d.soonestPickup);

            setBulkData(vehicleAvailabilities);

            if (didAvailabilitySucceed) {
              updateResponseErrors({ availabilityError: null });
            } else {
              const vehicle = res.data.content?.find(
                v =>
                  v.soonestDelivery?.errorMessage ||
                  v.soonestPickup?.errorMessage
              );

              const errorMessage =
                vehicle?.soonestDelivery?.errorMessage ||
                vehicle?.soonestPickup?.errorMessage;

              updateResponseErrors({ availabilityError: errorMessage });
            }
          } else {
            logError(new Error(res.data.errorMessage), {
              location: 'Availability',
              responseData: res.data
            });
            updateResponseErrors({ availabilityError: res.data.errorMessage });
          }
        })
        .catch(
          generateErrorLogger(
            'Availability Catch',
            {},
            {
              callback: () => {
                updateResponseErrors({
                  availabilityError: 'Failed to fetch availability.'
                });
              },
              controller
            }
          )
        );
    },
    [
      clientId,
      searchRequestId,
      setBulkData,
      shouldCallAvailability,
      stableVehicles,
      updateResponseErrors,
      userId
    ]
  );
};

export default useGetAvailability;
